import React, { useEffect, useMemo, useState } from 'react';
import { CalendarCheck, Search, Save, Loader2, Snowflake, Flame, AlertCircle, Check, X, Settings as SettingsIcon, DollarSign, Lock, Eye } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';

const STATUS_META = {
  current:   { label: 'Al corriente', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  expired:   { label: 'Vencido',      cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  unset:     { label: 'Sin fecha',    cls: 'bg-gray-100 text-charcoal-600 border-gray-200' },
  suspended: { label: 'Congelado',    cls: 'bg-rose-50 text-rose-700 border-rose-200' },
};

const CATEGORY_OPTIONS = [
  { key: 'active',    label: 'Activo' },
  { key: 'candidate', label: 'Candidato' },
  { key: 'senior',    label: 'Senior' },
  { key: 'honorary',  label: 'Honorario' },
  { key: 'observer',  label: 'Observador' },
];

function todayISO()         { return new Date().toISOString().slice(0, 10); }
function endOfThisYearISO() { return `${new Date().getFullYear()}-12-31`; }
function plusOneYear(fromISO) {
  const from = fromISO ? new Date(fromISO) : new Date();
  if (Number.isNaN(from.getTime())) return null;
  const d = new Date(from); d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}
function fmtMoney(n) {
  const v = Number(n || 0);
  return v.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });
}

export default function AdminDuesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingSlug, setSavingSlug] = useState('');
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [drafts, setDrafts] = useState({});
  const [settings, setSettings] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [access, setAccess] = useState({ internal_access_enforced: false, deadline: '', show_dues_on_profile: false });
  const [savingAccess, setSavingAccess] = useState(false);

  async function load() {
    setLoading(true); setError('');
    try {
      const [duesRes, setRes, accRes] = await Promise.all([
        api.get('/admin/dues'),
        api.get('/admin/dues/settings'),
        api.get('/admin/dues/access-settings').catch(() => ({ data: { internal_access_enforced: false, deadline: '' } })),
      ]);
      setRows(duesRes.data || []);
      setSettings(setRes.data || null);
      setAccess(accRes.data || { internal_access_enforced: false, deadline: '' });
      setDrafts({});
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error');
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function toggleEnforcement() {
    setSavingAccess(true);
    try {
      const { data } = await api.put('/admin/dues/access-settings', {
        internal_access_enforced: !access.internal_access_enforced,
      });
      setAccess(data);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error');
    } finally { setSavingAccess(false); }
  }

  async function toggleShowDues() {
    setSavingAccess(true);
    try {
      const { data } = await api.put('/admin/dues/access-settings', {
        show_dues_on_profile: !access.show_dues_on_profile,
      });
      setAccess(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error');
    } finally { setSavingAccess(false); }
  }

  const counts = useMemo(() => {
    const c = { all: rows.length, current: 0, expired: 0, unset: 0, suspended: 0 };
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  const totals = useMemo(() => {
    return rows.reduce((acc, r) => ({
      expected: acc.expected + Number(r.expected_amount || 0),
      paid:     acc.paid     + Number(r.amount_paid || 0),
      owed:     acc.owed     + Number(r.amount_owed || 0),
    }), { expected: 0, paid: 0, owed: 0 });
  }, [rows]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter !== 'all' && r.status !== filter) return false;
      if (!q) return true;
      return (
        (r.full_name || '').toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q) ||
        (r.city || '').toLowerCase().includes(q)
      );
    });
  }, [rows, filter, query]);

  function getCurrentValue(r, field) {
    const d = drafts[r.slug];
    if (d && d[field] !== undefined) return d[field];
    return r[field] ?? '';
  }
  function setDraft(slug, field, value) {
    setDrafts((prev) => ({ ...prev, [slug]: { ...(prev[slug] || {}), [field]: value } }));
  }

  async function saveRow(r) {
    setSavingSlug(r.slug); setError('');
    try {
      const body = {};
      const d = drafts[r.slug] || {};
      if (d.paid_until !== undefined)              body.paid_until = d.paid_until || '';
      if (d.membership_notes !== undefined)        body.membership_notes = d.membership_notes;
      if (d.amount_paid !== undefined)             body.amount_paid = Number(d.amount_paid) || 0;
      if (d.payment_commitment_date !== undefined) body.payment_commitment_date = d.payment_commitment_date || '';
      if (d.membership_category !== undefined)     body.membership_category = d.membership_category;
      if (d.internal_access !== undefined)         body.internal_access = d.internal_access || 'auto';
      await api.put(`/admin/dues/${r.slug}`, body);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error al guardar');
    } finally { setSavingSlug(''); }
  }

  async function quickSetPaid(r, isoDate) {
    setSavingSlug(r.slug);
    try {
      await api.put(`/admin/dues/${r.slug}`, { paid_until: isoDate });
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error');
    } finally { setSavingSlug(''); }
  }

  async function toggleFreeze(r) {
    setSavingSlug(r.slug);
    try {
      const url = r.is_active ? `/admin/dues/${r.slug}/freeze` : `/admin/dues/${r.slug}/unfreeze`;
      await api.post(url);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error');
    } finally { setSavingSlug(''); }
  }

  return (
    <div data-testid="admin-dues-page" className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-charcoal-800 flex items-center gap-2">
            <CalendarCheck size={22} className="text-cmic-600" /> Cuotas y membresía
          </h1>
          <p className="text-charcoal-500 mt-1 max-w-3xl text-sm">
            Define la cuota anual global por categoría y registra cuánto ha pagado cada miembro.
            Los miembros con cuota <span className="font-semibold text-amber-700">vencida</span> o
            <span className="font-semibold text-rose-700"> congelados</span> desaparecen del directorio público.
          </p>
        </div>
        <button
          onClick={() => setSettingsOpen(true)}
          data-testid="admin-dues-open-settings"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cmic-600 text-white text-sm font-medium hover:bg-cmic-700"
        >
          <SettingsIcon size={15} /> Configurar cuota anual
        </button>
      </div>

      {/* Totals summary */}
      <div className="grid sm:grid-cols-3 gap-3" data-testid="admin-dues-totals">
        <SummaryCard label="Esperado total" value={fmtMoney(totals.expected)} color="cmic" testid="dues-total-expected" />
        <SummaryCard label="Cobrado total"  value={fmtMoney(totals.paid)}     color="emerald" testid="dues-total-paid" />
        <SummaryCard label="Adeudado total" value={fmtMoney(totals.owed)}     color="amber" testid="dues-total-owed" />
      </div>

      {/* Internal-zone access enforcement toggle (OFF by default) */}
      <div className={`rounded-xl border p-4 flex items-start gap-3 ${access.internal_access_enforced ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid="dues-access-banner">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${access.internal_access_enforced ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}>
          <Lock size={17} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-charcoal-800">
            Control de acceso a la zona interna: {access.internal_access_enforced ? 'ACTIVADO' : 'DESACTIVADO'}
          </p>
          <p className="text-xs text-charcoal-600 mt-0.5 max-w-2xl">
            {access.internal_access_enforced
              ? <>Los miembros con cuota vencida (después de la fecha límite <strong>{access.deadline || '06-30'}</strong>) o marcados como "Bloquear" no entran al foro/zona interna ni aparecen en el directorio.</>
              : <>Mientras esté desactivado, <strong>nadie</strong> pierde acceso ni se oculta del directorio. Actívalo cuando el consejo termine de registrar quién pagó, quién debe y quién es baja. Se respeta la fecha límite <strong>{access.deadline || '06-30'}</strong>.</>}
          </p>
        </div>
        <button
          onClick={toggleEnforcement}
          disabled={savingAccess}
          role="switch"
          aria-checked={access.internal_access_enforced}
          data-testid="dues-access-toggle"
          className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors flex-shrink-0 disabled:opacity-50 ${access.internal_access_enforced ? 'bg-emerald-500' : 'bg-charcoal-300'}`}
          title={access.internal_access_enforced ? 'Desactivar control' : 'Activar control'}
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${access.internal_access_enforced ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>

      {/* Show dues/debt status on interpreter profiles (OFF by default) */}
      <div className={`rounded-xl border p-4 flex items-start gap-3 ${access.show_dues_on_profile ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200'}`} data-testid="dues-show-profile-banner">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${access.show_dues_on_profile ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-200 text-charcoal-500'}`}>
          <Eye size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-charcoal-800 text-sm">
            Mostrar estado de cuotas en el perfil: {access.show_dues_on_profile ? 'ACTIVADO' : 'DESACTIVADO'}
          </p>
          <p className="text-xs text-charcoal-600 mt-0.5">
            {access.show_dues_on_profile
              ? <>Los intérpretes ven su estado de adeudo en su panel y en el foro.</>
              : <>Mientras esté desactivado, <strong>no</strong> se muestra el adeudo ni en el panel del miembro ni en el foro (evita confusiones con quienes no deben). Actívalo cuando quieras que sea visible.</>}
          </p>
        </div>
        <button
          onClick={toggleShowDues}
          disabled={savingAccess}
          role="switch"
          aria-checked={access.show_dues_on_profile}
          data-testid="dues-show-profile-toggle"
          className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors flex-shrink-0 disabled:opacity-50 ${access.show_dues_on_profile ? 'bg-emerald-500' : 'bg-charcoal-300'}`}
          title={access.show_dues_on_profile ? 'Ocultar en perfiles' : 'Mostrar en perfiles'}
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${access.show_dues_on_profile ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-400" />
          <input
            type="text"
            placeholder="Buscar por nombre, email, ciudad…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="admin-dues-search"
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cmic-300"
          />
        </div>
        <div className="flex flex-wrap gap-1 text-xs">
          {['all', 'current', 'expired', 'unset', 'suspended'].map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`admin-dues-filter-${k}`}
              className={`px-3 py-1.5 rounded-lg border transition-colors ${filter === k ? 'bg-cmic-500 text-white border-cmic-500' : 'bg-white text-charcoal-600 border-gray-200 hover:bg-gray-50'}`}
            >
              {k === 'all' ? `Todos (${counts.all || 0})` : `${STATUS_META[k]?.label || k} (${counts[k] || 0})`}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 flex items-center gap-2" data-testid="admin-dues-error">
          <AlertCircle size={15} /> {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-cmic-500" size={32} /></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-right px-2 py-3 font-medium text-charcoal-400 text-[10px] w-10">#</th>
                  <th className="text-left px-3 py-3 font-medium text-charcoal-600">Miembro</th>
                  <th className="text-left px-3 py-3 font-medium text-charcoal-600">Categoría</th>
                  <th className="text-right px-3 py-3 font-medium text-charcoal-600">Esperado</th>
                  <th className="text-right px-3 py-3 font-medium text-charcoal-600">Pagado</th>
                  <th className="text-right px-3 py-3 font-medium text-charcoal-600">Adeudo</th>
                  <th className="text-left px-3 py-3 font-medium text-charcoal-600">Compromiso</th>
                  <th className="text-left px-3 py-3 font-medium text-charcoal-600">Estado / hasta</th>
                  <th className="text-left px-3 py-3 font-medium text-charcoal-600">Acceso interno</th>
                  <th className="text-right px-3 py-3 font-medium text-charcoal-600">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visible.map((r, idx) => {
                  const meta = STATUS_META[r.status] || STATUS_META.unset;
                  const isDirty = !!drafts[r.slug];
                  const paidDraft = getCurrentValue(r, 'amount_paid');
                  const owedLive = Math.max(Number(r.expected_amount || 0) - Number(paidDraft || 0), 0);
                  return (
                    <tr key={r.slug} data-testid={`admin-dues-row-${r.slug}`} className="hover:bg-gray-50/60 align-top">
                      <td className="px-2 py-3 text-right text-charcoal-400 text-xs tabular-nums" data-testid={`admin-dues-num-${r.slug}`}>{idx + 1}</td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          {r.photo_url ? (
                            <img src={r.photo_url} alt="" className="w-8 h-8 rounded-lg object-cover" />
                          ) : (
                            <div className="w-8 h-8 rounded-lg bg-gray-200" />
                          )}
                          <div className="min-w-0">
                            <div className="font-medium text-charcoal-800 truncate text-[13px]">{r.full_name}</div>
                            <div className="text-[10px] text-charcoal-500 truncate">{r.email || r.city}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <select
                          value={getCurrentValue(r, 'membership_category') || 'active'}
                          onChange={(e) => setDraft(r.slug, 'membership_category', e.target.value)}
                          data-testid={`admin-dues-category-${r.slug}`}
                          className="border border-gray-200 rounded-lg px-2 py-1 text-xs"
                        >
                          {CATEGORY_OPTIONS.map((opt) => (
                            <option key={opt.key} value={opt.key}>{opt.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-3 text-right text-charcoal-700 font-medium tabular-nums" data-testid={`admin-dues-expected-${r.slug}`}>
                        {fmtMoney(r.expected_amount)}
                      </td>
                      <td className="px-3 py-3">
                        <input
                          type="number"
                          min="0"
                          step="100"
                          value={paidDraft}
                          onChange={(e) => setDraft(r.slug, 'amount_paid', e.target.value)}
                          data-testid={`admin-dues-paidamt-${r.slug}`}
                          className="w-24 border border-gray-200 rounded-lg px-2 py-1 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-cmic-300"
                        />
                      </td>
                      <td className={`px-3 py-3 text-right tabular-nums font-semibold ${owedLive > 0 ? 'text-amber-700' : 'text-emerald-700'}`} data-testid={`admin-dues-owed-${r.slug}`}>
                        {fmtMoney(owedLive)}
                      </td>
                      <td className="px-3 py-3">
                        <input
                          type="date"
                          value={(getCurrentValue(r, 'payment_commitment_date') || '').slice(0, 10)}
                          onChange={(e) => setDraft(r.slug, 'payment_commitment_date', e.target.value)}
                          data-testid={`admin-dues-commit-${r.slug}`}
                          className="border border-gray-200 rounded-lg px-2 py-1 text-xs"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded border ${meta.cls}`}>
                          {meta.label}
                        </span>
                        <input
                          type="date"
                          value={getCurrentValue(r, 'paid_until')?.slice(0, 10) || ''}
                          onChange={(e) => setDraft(r.slug, 'paid_until', e.target.value)}
                          data-testid={`admin-dues-paid-${r.slug}`}
                          className="mt-1 border border-gray-200 rounded-lg px-2 py-1 text-xs"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <select
                          value={getCurrentValue(r, 'internal_access') || 'auto'}
                          onChange={(e) => setDraft(r.slug, 'internal_access', e.target.value)}
                          data-testid={`admin-dues-access-${r.slug}`}
                          className="border border-gray-200 rounded-lg px-2 py-1 text-xs"
                        >
                          <option value="auto">Automático</option>
                          <option value="allow">Permitir</option>
                          <option value="block">Bloquear</option>
                        </select>
                        {access.internal_access_enforced && (
                          <div className={`mt-1 text-[10px] font-medium ${r.internal_blocked ? 'text-rose-600' : 'text-emerald-600'}`}>
                            {r.internal_blocked ? '● Sin acceso' : '● Con acceso'}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1">
                          {isDirty && (
                            <button onClick={() => saveRow(r)} disabled={savingSlug === r.slug}
                              data-testid={`admin-dues-save-${r.slug}`} title="Guardar"
                              className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
                            >
                              {savingSlug === r.slug ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                            </button>
                          )}
                          <button onClick={() => quickSetPaid(r, endOfThisYearISO())} disabled={savingSlug === r.slug}
                            data-testid={`admin-dues-quickpay-year-${r.slug}`} title={`Marcar pagada hasta fin de ${new Date().getFullYear()}`}
                            className="p-1.5 rounded-lg text-cmic-600 hover:bg-cmic-50 disabled:opacity-50"
                          >
                            <Check size={14} />
                          </button>
                          <button onClick={() => quickSetPaid(r, plusOneYear(getCurrentValue(r, 'paid_until') || todayISO()))} disabled={savingSlug === r.slug}
                            data-testid={`admin-dues-quickpay-12m-${r.slug}`} title="Sumar 12 meses"
                            className="p-1.5 rounded-lg text-cmic-600 hover:bg-cmic-50 disabled:opacity-50 text-[11px] font-semibold"
                          >
                            +12m
                          </button>
                          <button onClick={() => toggleFreeze(r)} disabled={savingSlug === r.slug}
                            data-testid={`admin-dues-freeze-${r.slug}`} title={r.is_active ? 'Congelar' : 'Reactivar'}
                            className={`p-1.5 rounded-lg disabled:opacity-50 ${r.is_active ? 'text-rose-600 hover:bg-rose-50' : 'text-emerald-600 hover:bg-emerald-50'}`}
                          >
                            {r.is_active ? <Snowflake size={14} /> : <Flame size={14} />}
                          </button>
                          {isDirty && (
                            <button onClick={() => setDrafts((p) => { const { [r.slug]: _, ...rest } = p; return rest; })}
                              title="Descartar cambios"
                              className="p-1.5 rounded-lg text-charcoal-400 hover:bg-gray-100"
                            >
                              <X size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {visible.length === 0 && (
                  <tr><td colSpan="10" className="px-4 py-10 text-center text-charcoal-500 text-sm">Sin resultados.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="text-[11px] text-charcoal-400">
        Atajos: ✓ = marcar pagada hasta fin de {new Date().getFullYear()}. +12m = sumar 12 meses. Edita el monto pagado por miembro para que se calcule su adeudo automáticamente.
      </div>

      {settingsOpen && (
        <DuesSettingsModal
          current={settings}
          onClose={() => setSettingsOpen(false)}
          onSaved={async () => { setSettingsOpen(false); await load(); }}
        />
      )}
    </div>
  );
}

function SummaryCard({ label, value, color = 'cmic', testid }) {
  const colors = {
    cmic:    'from-cmic-50 to-white border-cmic-100 text-cmic-700',
    emerald: 'from-emerald-50 to-white border-emerald-100 text-emerald-700',
    amber:   'from-amber-50 to-white border-amber-100 text-amber-700',
  };
  return (
    <div className={`rounded-xl border bg-gradient-to-br p-4 ${colors[color]}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-wider font-semibold opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function DuesSettingsModal({ current, onClose, onSaved }) {
  const [form, setForm] = useState({
    year:              current?.year || new Date().getFullYear(),
    active_amount:     current?.active_amount || 0,
    candidate_pct:     current?.candidate_pct ?? 50,
    senior_pct:        current?.senior_pct ?? 50,
    honorary_pct:      current?.honorary_pct ?? 50,
    observer_pct:      current?.observer_pct ?? 30,
    due_date:          current?.due_date || '06-30',
    late_fee_pct_per_month: current?.late_fee_pct_per_month ?? 5,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  function set(k) { return (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'number' ? Number(e.target.value) : e.target.value })); }
  const base = Number(form.active_amount) || 0;
  const pct = (p) => Math.round(base * (Number(p) || 0) / 100);

  async function submit(e) {
    e.preventDefault();
    setSaving(true); setErr('');
    try {
      await api.put('/admin/dues/settings', form);
      onSaved();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || 'Error');
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="admin-dues-settings-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="font-bold text-charcoal-800 flex items-center gap-2 text-base">
            <DollarSign size={18} className="text-cmic-600" /> Cuota anual {form.year}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4 text-sm">
          {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-2 text-xs">{err}</div>}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Año vigente">
              <input type="number" value={form.year} onChange={set('year')}
                data-testid="dues-settings-year"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            </Field>
            <Field label="Cuota Activo (MXN) — 100%">
              <input type="number" min="0" step="100" value={form.active_amount} onChange={set('active_amount')}
                data-testid="dues-settings-active-amount"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            </Field>
          </div>
          <div className="border-t border-gray-100 pt-3">
            <p className="font-semibold text-charcoal-700 mb-2 text-xs uppercase tracking-wider">% por categoría (sobre cuota Activo)</p>
            <div className="grid grid-cols-2 gap-3">
              <PctField label="Candidato"  pct={form.candidate_pct} onChange={set('candidate_pct')} testid="dues-settings-candidate-pct" preview={pct(form.candidate_pct)} />
              <PctField label="Senior"     pct={form.senior_pct}    onChange={set('senior_pct')}    testid="dues-settings-senior-pct"    preview={pct(form.senior_pct)} />
              <PctField label="Honorario"  pct={form.honorary_pct}  onChange={set('honorary_pct')}  testid="dues-settings-honorary-pct"  preview={pct(form.honorary_pct)} />
              <PctField label="Observador" pct={form.observer_pct}  onChange={set('observer_pct')}  testid="dues-settings-observer-pct"  preview={pct(form.observer_pct)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 border-t border-gray-100 pt-3">
            <Field label="Fecha límite de pago (MM-DD)">
              <input type="text" value={form.due_date} onChange={set('due_date')} placeholder="06-30"
                pattern="\d{2}-\d{2}" data-testid="dues-settings-due-date"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono" />
            </Field>
            <Field label="Recargo mensual (%)">
              <input type="number" min="0" max="100" step="0.5" value={form.late_fee_pct_per_month} onChange={set('late_fee_pct_per_month')}
                data-testid="dues-settings-late-fee"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            </Field>
          </div>
          <div className="bg-cmic-50 border border-cmic-100 rounded-lg p-3 text-xs text-cmic-800 leading-relaxed">
            <strong>Reglamento de Tesorería (Art. 16):</strong> los miembros deben pagar a más tardar el 30 de junio del año correspondiente; cualquier pago posterior conlleva el recargo definido arriba por cada mes de retraso.
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-charcoal-600 hover:bg-gray-100 text-sm">Cancelar</button>
            <button type="submit" disabled={saving}
              data-testid="dues-settings-save"
              className="px-4 py-2 rounded-lg bg-cmic-600 text-white font-medium text-sm hover:bg-cmic-700 disabled:opacity-60">
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-charcoal-600 mb-1">{label}</span>
      {children}
    </label>
  );
}

function PctField({ label, pct, onChange, testid, preview }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-charcoal-600 mb-1 flex items-center justify-between">
        <span>{label}</span>
        <span className="text-[10px] text-charcoal-400 font-normal">≈ ${Number(preview).toLocaleString('es-MX')}</span>
      </span>
      <div className="relative">
        <input type="number" min="0" max="100" step="1" value={pct} onChange={onChange}
          data-testid={testid}
          className="w-full border border-gray-200 rounded-lg pl-3 pr-7 py-2 text-sm" />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal-400 text-xs">%</span>
      </div>
    </label>
  );
}
